<script setup>
  import {onBeforeUnmount, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentBarcodeScanner from "@components/BarcodeScanner/BarcodeScanner.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {MedSpinner} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientinputComponentScannerDialog",
  });

  defineProps({
    dialogTitle: {
      type: String,
      required: true,
    },
    dialogType: {
      type: String,
      default: "",
    },
    formats: {
      type: Array,
      required: true,
    },
    loaderClass: {
      type: String,
      default: "",
    },
    scannerClass: {
      type: String,
      default: "",
    },
    primaryButtonDisabled: {
      type: Boolean,
      default: false,
    },
    paused: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["detect", "okButtonClicked"]);

  const refScannerDialog = ref(null);

  const cameraReady = ref(false);
  const errorMessage = ref(null);
  const setupTime = ref(0);
  let temporaryTime = 0;

  const open = () => {
    cameraReady.value = false;
    errorMessage.value = null;
    refScannerDialog.value.open();
    temporaryTime = Date.now();
  };

  const close = () => {
    refScannerDialog.value.close();
  };

  const onReady = () => {
    cameraReady.value = true;
    setupTime.value = Date.now() - temporaryTime;
  };

  const onError = (error) => {
    errorMessage.value = error.message;
  };

  onBeforeUnmount(() => {
    close();
  });

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refScannerDialog"
    :dialog-title="dialogTitle"
    :dialog-type="dialogType"
    :primary-button-disabled="primaryButtonDisabled"
    :test-id="getTestId('scanner')"
    @ok-button-clicked="$emit('okButtonClicked')"
  >
    <template #content>
      <div v-if="errorMessage">
        <ComponentAlert type="error" :test-id="getTestId('scanner-dialog')">{{ errorMessage }}</ComponentAlert>
      </div>

      <div v-else-if="!cameraReady" class="flex items-center justify-center" :class="loaderClass">
        <MedSpinner size="3xl" :data-test="getTestId('scanner-dialog-camera-spinner')" />
      </div>

      <div v-show="cameraReady && !errorMessage" class="flex flex-col gap-3">
        <slot />

        <ComponentBarcodeScanner
          :class="scannerClass"
          :barcode-formats="formats"
          :paused="paused"
          :use-patient-text="true"
          @camera-on="onReady"
          @detect="$emit('detect', $event)"
          @error="onError"
        >
          <div v-show="paused" class="flex size-full flex-col items-center justify-center gap-3 bg-white/75 p-3">
            <slot name="paused">
              <MedSpinner size="3xl" :data-test="getTestId('scanner-dialog-paused-spinner')" />
            </slot>
          </div>
        </ComponentBarcodeScanner>
      </div>
    </template>
  </ComponentDialog>
</template>
